<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { get_packages, parsePackageswithCharacteristics } from '@/services/option';
import { import_csv } from '@/services/assessments';
import { swalSuccess, swalError } from '@/helper';

const packages = ref([]);
const selected_package_id = ref('');
const selected_char_id = ref('');

const isSubmitting = ref(false);
const isLoading = ref(true);

const form = ref({
  import_type: 'assessments',
  trait_id: '',
  file: null
});

onMounted(async () => {
  let response = await get_packages();
  if (response.error) {
    if (!response.isRedirecting) swalError(response.message || 'Failed to load packages');
    isLoading.value = false;
    return;
  }
  
  let packagesData = await parsePackageswithCharacteristics(response.result);
  packages.value = packagesData || [];
  isLoading.value = false;
});

const currentCharacteristics = computed(() => {
  if (!selected_package_id.value) return [];
  const pkg = packages.value.find(p => p.id === selected_package_id.value);
  return pkg ? pkg.characters : [];
});

const currentTraits = computed(() => {
  if (!selected_char_id.value) return [];
  const char = currentCharacteristics.value.find(c => c.id === selected_char_id.value);
  return char ? char.traits : [];
});

watch(selected_package_id, () => {
  selected_char_id.value = '';
  form.value.trait_id = '';
});

watch(selected_char_id, () => {
  form.value.trait_id = '';
});

function handleFileUpload(event) {
  form.value.file = event.target.files[0];
}

async function startImport() {
  if (!form.value.file) {
    swalError('Please select a CSV file to upload.');
    return;
  }
  if (form.value.import_type === 'assessments' && !form.value.trait_id) {
    swalError('Please select a Trait when importing Assessments.');
    return;
  }

  isSubmitting.value = true;
  let res = await import_csv(form.value.file, form.value.import_type, form.value.trait_id);
  
  if (res.error) {
    swalError(res.message || 'Import failed');
  } else {
    swalSuccess(res.result?.data?.message || 'Import successful');
    // Clear form
    form.value.file = null;
    document.getElementById('csv-upload-input').value = '';
  }
  isSubmitting.value = false;
}
</script>

<template>
  <div class="container-fluid">
    <Breadcrumbs 
      main="Assessments" 
      title="Import Data" 
    />
    
    <div class="row">
      <div class="col-sm-12 col-xl-6">
        <LoadingCard v-if="isLoading" />
        <div v-else class="card">
          <div class="card-header border-bottom">
            <h5>Import Assessments / Indicators (CSV)</h5>
            <span>Ensure your CSV file matches the structured template.</span>
          </div>
          <div class="card-body">
            <form @submit.prevent="startImport" class="theme-form">
              <div class="mb-3">
                <label class="form-label">Import Type</label>
                <select class="form-select" v-model="form.import_type" required>
                  <option value="assessments">Assessments</option>
                  <option value="indicators">Indicators</option>
                </select>
              </div>

              <div v-if="form.import_type === 'assessments'">
                <div class="mb-3">
                  <label class="form-label">Package</label>
                  <select class="form-select" v-model="selected_package_id" required>
                    <option value="" disabled>Select Package...</option>
                    <option v-for="pkg in packages" :key="pkg.id" :value="pkg.id">
                      {{ pkg.options?.name?.value || pkg.package_type }}
                    </option>
                  </select>
                </div>

                <div class="mb-3" v-if="selected_package_id">
                  <label class="form-label">Characteristic</label>
                  <select class="form-select" v-model="selected_char_id" required>
                    <option value="" disabled>Select Characteristic...</option>
                    <option v-for="char in currentCharacteristics" :key="char.id" :value="char.id">
                      {{ char.name }}
                    </option>
                  </select>
                </div>

                <div class="mb-3" v-if="selected_char_id">
                  <label class="form-label">Trait</label>
                  <select class="form-select" v-model="form.trait_id" required>
                    <option value="" disabled>Select Trait...</option>
                    <option v-for="trait in currentTraits" :key="trait.id" :value="trait.id">
                      {{ trait.name }}
                    </option>
                  </select>
                  <small class="text-muted">Required so new assessments are mapped to this specific trait.</small>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">CSV File</label>
                <input class="form-control" type="file" accept=".csv" @change="handleFileUpload" id="csv-upload-input" required>
              </div>

              <div class="mt-4">
                <button class="btn btn-primary" type="submit" :disabled="isSubmitting">
                  <span v-if="!isSubmitting">Upload & Import</span>
                  <div v-else class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
